import maplibregl, { type Map as MlMap, type LngLatLike, type Popup } from "maplibre-gl";

/**
 * Map-marker cards on phones (v0.9.4a เก็บงาน #3). A dam / station / gauge card used
 * to open UNDER the expanded bottom sheet. Now, on a phone, tapping a marker:
 *  1. collapses the sheet (ui.ts listens for "fs-sheet-collapse") — the result stays
 *     in the sheet, one tap on ⌃ brings it back;
 *  2. moves the tapped point near the top of the map and anchors the card BELOW it,
 *     so the card sits in the free map area above the 💧/▶ buttons;
 *  3. flags the tap so the map's own "analyse this point" click runs quietly
 *     (no sheet expand, no re-centre) instead of covering the card again.
 * Desktop: unchanged (a normal popup at the point).
 */
const PHONE = "(max-width: 767px)";
const TOP_PX = 84; // just under the search bar
let lastMarkerTap = 0;

export function isPhone(): boolean { return window.matchMedia(PHONE).matches; }
/** true right after a marker card was opened by a tap (→ analyse quietly). */
export function markerTapRecent(): boolean { return Date.now() - lastMarkerTap < 800; }

export function openMapPopup(map: MlMap, lngLat: LngLatLike, html: string, maxWidth: string): Popup {
  if (!isPhone()) {
    return new maplibregl.Popup({ closeButton: true, maxWidth, className: "fs-popup" }).setLngLat(lngLat).setHTML(html).addTo(map);
  }
  lastMarkerTap = Date.now();
  window.dispatchEvent(new Event("fs-sheet-collapse"));
  // the compact ⓘ credits box starts open on phones and covers the top of the map,
  // exactly where the card goes → fold it (ⓘ reopens it; credits stay one tap away)
  document.querySelector(".maplibregl-ctrl-attrib.maplibregl-compact-show")?.classList.remove("maplibregl-compact-show");
  const p = map.project(lngLat);
  // centre horizontally too, so a 260–280px card fits a 320px-wide screen
  map.panBy([p.x - map.getContainer().clientWidth / 2, p.y - TOP_PX], { duration: 250 });
  const popup = new maplibregl.Popup({ closeButton: true, maxWidth, className: "fs-popup", anchor: "top" }).setLngLat(lngLat).setHTML(html).addTo(map);
  // the card spans the narrow screen → the top-right group steps out while it is open (v0.9.9 A4)
  document.body.classList.add("card-open");
  popup.once("close", () => { if (!document.querySelector(".fs-popup")) document.body.classList.remove("card-open"); });
  // keep a long card above the 💧/▶ buttons + collapsed sheet: cap its height to the
  // free map area and let it scroll (measured, so it follows the real layout)
  const fit = (): void => {
    const content = popup.getElement()?.querySelector<HTMLElement>(".maplibregl-popup-content");
    if (!content) return;
    const tops = ["#locate-fab", "#flow-fab", "#sos-fab", ".sheet"].map((q) => document.querySelector(q)?.getBoundingClientRect().top)
      .filter((v): v is number => typeof v === "number" && v > 0);
    const limit = Math.min(window.innerHeight, ...tops) - 8;
    const room = Math.max(120, limit - content.getBoundingClientRect().top);
    content.style.maxHeight = `${Math.floor(room)}px`;
    content.style.overflowY = "auto";
  };
  map.once("moveend", () => requestAnimationFrame(fit));
  setTimeout(fit, 400); // also after the sheet's collapse transition
  return popup;
}
